import * as React from "react";
import { Toaster as SonnerToaster, toast } from "sonner";
import { cn } from "../lib/cn.js";

export interface ToasterProps
  extends React.ComponentProps<typeof SonnerToaster> {}

/**
 * App-level toaster. Wraps sonner with our token-driven styling.
 * Mount once near the root of each app.
 */
export const Toaster = ({
  className,
  position = "top-center",
  toastOptions,
  closeButton = true,
  ...props
}: ToasterProps) => (
  <SonnerToaster
    position={position}
    closeButton={closeButton}
    className={cn("toaster group", className)}
    toastOptions={{
      ...toastOptions,
      classNames: {
        toast: cn(
          "group toast",
          "rounded-[var(--radius-md)] border border-[var(--border-default)]",
          "bg-[var(--bg-elevated)] text-[var(--text-primary)]",
          "shadow-[var(--shadow-md)]",
          "p-[var(--space-4)] gap-[var(--space-3)]",
          "text-[var(--text-body-sm)]",
        ),
        title: "font-medium",
        description: "text-[var(--text-secondary)]",
        actionButton: cn(
          "rounded-[var(--radius-sm)] px-[var(--space-3)] py-[var(--space-1)]",
          "bg-[var(--accent)] text-[var(--accent-fg)]",
          "text-[var(--text-caption)] font-medium",
        ),
        cancelButton: cn(
          "rounded-[var(--radius-sm)] px-[var(--space-3)] py-[var(--space-1)]",
          "bg-[var(--bg-sunken)] text-[var(--text-secondary)]",
          "text-[var(--text-caption)] font-medium",
        ),
        success: "border-l-2 border-l-[var(--success)]",
        error: "border-l-2 border-l-[var(--danger)]",
        warning: "border-l-2 border-l-[var(--warning)]",
        info: "border-l-2 border-l-[var(--info)]",
        closeButton: cn(
          "absolute top-1.5 right-1.5 size-8 rounded-[var(--radius-sm)]",
          "border border-[var(--border-subtle)] bg-[var(--bg-surface)]",
          "text-[var(--text-tertiary)] hover:text-[var(--text-primary)]",
          "hover:bg-[var(--bg-hover)] transition-colors",
          "flex items-center justify-center",
          "[&>svg]:size-4",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]",
        ),
        ...toastOptions?.classNames,
      },
    }}
    {...props}
  />
);
Toaster.displayName = "Toaster";

export { toast };
